import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { postsApi, userApi } from '../services/api';
import hiveService from '../services/hive';
import PostCard from '../components/PostCard/PostCard';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import WalletInfo from '../components/WalletInfo/WalletInfo';
import Lightbox from '../components/Lightbox/Lightbox';
import MarkdownRenderer from '../components/MarkdownRenderer/MarkdownRenderer';
import AddToListModal from '../components/AddToListModal/AddToListModal';
import FollowList from './FollowList';
import './UserProfile.css';
import { FEED_DISPLAY_CONFIG, getDefaultEnabledFeedKeys } from '../config/feed-details';
import PostSkeleton from '../components/PostSkeleton/PostSkeleton';
import { useUserMana } from '../hooks/useUserMana';

const UserProfile = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { username, feedType: routeFeedType, blogType: routeBlogType, postFilter: routePostFilter } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const { autoload_more = true, addMutedUser, removeMutedUser, isUserMuted } = useSettings();
  const { manaData, isRefreshingMana, handleRefreshMana } = useUserMana(username);
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [profilePinnedPosts, setProfilePinnedPosts] = useState([]);
  const [loadingProfilePinnedPosts, setLoadingProfilePinnedPosts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMoreShort, setLoadingMoreShort] = useState(false);
  const [loadingMoreBlog, setLoadingMoreBlog] = useState(false);

  const [loadingBlogTab, setLoadingBlogTab] = useState(false);
  const [hasMoreShort, setHasMoreShort] = useState(true);
  const [hasMoreBlog, setHasMoreBlog] = useState(true);
  const [shortOffset, setShortOffset] = useState(0);
  const [shortStartPermlink, setShortStartPermlink] = useState(null);
  const [blogStartPermlink, setBlogStartPermlink] = useState(null);
  const [error, setError] = useState('');
  const [isFollowing, setIsFollowing] = useState(false);
  const [shortFeedType, setShortFeedType] = useState((routeFeedType || 'flex').toLowerCase());
  const normalizePostFilter = (value) => {
    const v = (value || 'posts').toLowerCase();
    if (v === 'comments' || v === 'all') return v;
    return 'posts';
  };
  const [postFilter, setPostFilter] = useState(normalizePostFilter(routePostFilter));
  const normalizeBlogType = (value) => {
    const normalized = (value || 'posts').toLowerCase();
    if (normalized === 'all' || normalized === 'reblogs' || normalized === 'pinned' || normalized === 'comments') return normalized;
    return 'posts';
  };
  const [blogType, setBlogType] = useState(normalizeBlogType(routeBlogType));
  const [showPinnedBlogPosts, setShowPinnedBlogPosts] = useState(false);
  const [showWalletInfo, setShowWalletInfo] = useState(false);
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [showRawHive, setShowRawHive] = useState(false);
  const [showProfileImageLightbox, setShowProfileImageLightbox] = useState(false);
  const [showCoverLightbox, setShowCoverLightbox] = useState(false);
  const [enabledFeeds, setEnabledFeeds] = useState([]);
  const [loadingTab, setLoadingTab] = useState(false);
  const isProfileMuted = isUserMuted(username);

  const normalizedRouteFeedType = (routeFeedType || 'flex').toLowerCase();
  const normalizedRouteBlogType = normalizeBlogType(routeBlogType);
  const normalizedRoutePostFilter = normalizePostFilter(routePostFilter);
  const isFollowersRoute = location.pathname.includes(`/user/${username}/followers`);
  const isFollowingRoute = location.pathname.includes(`/user/${username}/following`);
  const isBlogRoute = location.pathname.includes(`/user/${username}/blog`);
  const activeTab = isFollowersRoute ? 'followers' : isFollowingRoute ? 'following' : isBlogRoute ? 'blog' : 'short';

  const fetchShortPosts = useCallback(async (feedType, filter = 'posts') => {
    try {
      const result = await postsApi.getUserShortPostsByFeed(username, feedType, 20, 0, null, 'auto', filter);
      setPosts(result.posts || []);
      setHasMoreShort(result.hasMore !== false);
      setShortOffset(result.nextOffset || 0);
      setShortStartPermlink(result.nextStartPermlink || null);
    } catch (err) {
      console.error('Error fetching short posts:', err);
      setPosts([]);
      setHasMoreShort(false);
    }
  }, [username]);

  // Fetch enabled feeds from config and enrich with FEED_DISPLAY_CONFIG
  // Only show feeds on profile where showOnProfile AND enabledByDefault are true
  useEffect(() => {
    const isProfileFeed = (key) => {
      const cfg = FEED_DISPLAY_CONFIG[key];
      return cfg?.showOnProfile && cfg?.enabledByDefault;
    };

    const enrichFeed = (f) => ({
      ...f,
      label: FEED_DISPLAY_CONFIG[f.key]?.label || f.label,
    });

    const fetchEnabledFeeds = async () => {
      try {
        const response = await fetch('/api/feed/feeds-config');
        const feeds = await response.json();
        setEnabledFeeds(
          feeds
            .filter(f => f.enabled !== false && isProfileFeed(f.key))
            .map(enrichFeed)
        );
      } catch (error) {
        console.error('Error fetching feeds config:', error);
        // Fallback using FEED_DISPLAY_CONFIG defaults
        setEnabledFeeds(
          getDefaultEnabledFeedKeys()
            .filter(key => FEED_DISPLAY_CONFIG[key]?.showOnProfile)
            .map(key => ({
              key,
              label: FEED_DISPLAY_CONFIG[key]?.label || key,
            }))
        );
      }
    };
    
    fetchEnabledFeeds();
  }, []);

  const handleFeedChange = (feedType) => {
    // Keep selected profile short feed in the URL so refresh preserves it.
    // Always reset to default (top-level) filter when switching feeds.
    if (feedType === normalizedRouteFeedType && normalizedRoutePostFilter === 'posts') return;
    setShortFeedType(feedType);
    navigate(`/user/${username}/feed/${feedType}`);
  };

  const handlePostFilterChange = (filter) => {
    const normalized = normalizePostFilter(filter);
    if (normalized === normalizedRoutePostFilter) return;
    setPostFilter(normalized);
    const filterSegment = normalized !== 'posts' ? `/${normalized}` : '';
    navigate(`/user/${username}/feed/${shortFeedType}${filterSegment}`);
  };

  const handleCommentPosted = () => {
    // Refresh posts after a comment is posted
    fetchShortPosts(shortFeedType);
  };

  const loadMoreShortObserverRef = useRef(null);
  const loadMoreBlogObserverRef = useRef(null);

  const loadMoreShortPosts = useCallback(async () => {
    if (loadingMoreShort || !hasMoreShort || posts.length === 0) return;
    
    setLoadingMoreShort(true);
    try {
      const result = await postsApi.getUserShortPostsByFeed(
        username, 
        shortFeedType, 
        20, 
        shortOffset,
        shortStartPermlink,
        'auto',
        postFilter
      );
      const newPosts = result.posts || [];
      setPosts(prev => [...prev, ...newPosts]);
      setHasMoreShort(result.hasMore !== false);
      setShortOffset(result.nextOffset || 0);
      setShortStartPermlink(result.nextStartPermlink || null);
    } catch (err) {
      console.error('Error loading more short posts:', err);
    } finally {
      setLoadingMoreShort(false);
    }
  }, [loadingMoreShort, hasMoreShort, posts.length, username, shortFeedType, shortOffset, shortStartPermlink, postFilter]);

  useEffect(() => {
    if (!autoload_more || !hasMoreShort || loadingMoreShort || loadingTab || posts.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreShortPosts();
        }
      },
      { rootMargin: '800px' }
    );

    const target = loadMoreShortObserverRef.current;
    if (target) observer.observe(target);

    return () => { if (target) observer.unobserve(target); };
  }, [autoload_more, hasMoreShort, loadingMoreShort, loadingTab, posts.length, loadMoreShortPosts]);

  const fetchBlogPosts = useCallback(async (type) => {
    setLoadingBlogTab(true);
    try {
      const includeReblogs = type === 'all';
      const reblogsOnly = type === 'reblogs';
      const isComments = type === 'comments';
      const blogPostsData = await postsApi.getUserBlogPosts(
        username, 10,
        includeReblogs || reblogsOnly,
        null,
        reblogsOnly,
        isComments ? 'comments' : type,
        'auto'
      );
      setBlogPosts(blogPostsData.posts || []);
      setHasMoreBlog((blogPostsData.posts || []).length === 10);
      setBlogStartPermlink(blogPostsData.posts && blogPostsData.posts.length > 0 ? blogPostsData.posts[blogPostsData.posts.length - 1].permlink : null);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setBlogPosts([]);
      setHasMoreBlog(false);
    } finally {
      setLoadingBlogTab(false);
    }
  }, [username]);

  const loadMoreBlogPosts = useCallback(async () => {
    if (loadingMoreBlog || !hasMoreBlog || blogPosts.length === 0) return;
    
    setLoadingMoreBlog(true);
    try {
      const includeReblogs = blogType === 'all';
      const reblogsOnly = blogType === 'reblogs';
      const isComments = blogType === 'comments';
      const blogPostsData = await postsApi.getUserBlogPosts(
        username, 
        10, 
        includeReblogs || reblogsOnly,
        blogStartPermlink,
        reblogsOnly,
        isComments ? 'comments' : blogType,
        'auto'
      );
      const newPosts = blogPostsData.posts || [];
      setBlogPosts(prev => [...prev, ...newPosts]);
      setHasMoreBlog(newPosts.length === 10);
      setBlogStartPermlink(newPosts.length > 0 ? newPosts[newPosts.length - 1].permlink : null);
    } catch (err) {
      console.error('Error loading more blog posts:', err);
    } finally {
      setLoadingMoreBlog(false);
    }
  }, [loadingMoreBlog, hasMoreBlog, blogPosts.length, username, blogType, blogStartPermlink]);

  useEffect(() => {
    if (!autoload_more || !hasMoreBlog || loadingMoreBlog || loadingBlogTab || blogPosts.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreBlogPosts();
        }
      },
      { rootMargin: '800px' }
    );

    const target = loadMoreBlogObserverRef.current;
    if (target) observer.observe(target);

    return () => { if (target) observer.unobserve(target); };
  }, [autoload_more, hasMoreBlog, loadingMoreBlog, loadingBlogTab, blogPosts.length, loadMoreBlogPosts]);

  const fetchUserData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const profileData = await userApi.getProfile(username);
      setProfile(profileData.user);

      // Check follow status via dedicated DB-backed endpoint (no caching, no 100-limit problem)
      if (isAuthenticated && currentUser && currentUser.username !== username) {
        try {
          const followData = await userApi.checkFollows(currentUser.username, username);
          setIsFollowing(!!followData.isFollowing);
        } catch {
          setIsFollowing(false);
        }
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load user profile.');
    } finally {
      setLoading(false);
    }
  }, [username, isAuthenticated, currentUser]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    if (!profile) {
      setProfilePinnedPosts([]);
      return;
    }

    const pinnedRefs = Array.isArray(profile.pinnedPosts)
      ? profile.pinnedPosts
          .filter((p) => p && typeof p.author === 'string' && typeof p.permlink === 'string')
          .slice(0, 10)
      : [];

    if (pinnedRefs.length === 0) {
      setProfilePinnedPosts([]);
      return;
    }

    let cancelled = false;

    const loadPinnedPosts = async () => {
      setLoadingProfilePinnedPosts(true);
      try {
        const settled = await Promise.allSettled(
          pinnedRefs.map(({ author, permlink }) => postsApi.getPost(author, permlink, false))
        );
        if (cancelled) return;

        const loaded = settled
          .filter((r) => r.status === 'fulfilled' && r.value?.post)
          .map((r) => ({
            ...r.value.post,
            stats: {
              ...(r.value.post.stats || {}),
              is_pinned: true
            }
          }));

        setProfilePinnedPosts(loaded);
      } catch (err) {
        if (!cancelled) {
          console.warn('Failed loading pinned profile posts:', err?.message || err);
          setProfilePinnedPosts([]);
        }
      } finally {
        if (!cancelled) setLoadingProfilePinnedPosts(false);
      }
    };

    loadPinnedPosts();
    return () => {
      cancelled = true;
    };
  }, [profile]);

  // Reset pagination cursors when username changes.
  useEffect(() => {
    setShortFeedType(normalizedRouteFeedType);
    setBlogType(normalizedRouteBlogType);
    setPostFilter(normalizedRoutePostFilter);
    setShortOffset(0);
    setShortStartPermlink(null);
    setBlogStartPermlink(null);
    setLoadingTab(false);
  }, [username, normalizedRouteFeedType, normalizedRouteBlogType, normalizedRoutePostFilter]);

  useEffect(() => {
    setShortFeedType(normalizedRouteFeedType);
  }, [normalizedRouteFeedType]);

  useEffect(() => {
    setBlogType(normalizedRouteBlogType);
  }, [normalizedRouteBlogType]);

  useEffect(() => {
    setPostFilter(normalizedRoutePostFilter);
  }, [normalizedRoutePostFilter]);

  // Normalize legacy profile URLs to explicit tab routes.
  useEffect(() => {
    if (!username) return;
    if (location.pathname === `/user/${username}`) {
      navigate(`/user/${username}/feed/${normalizedRouteFeedType || 'flex'}`, { replace: true });
      return;
    }
    if (location.pathname === `/user/${username}/blog`) {
      navigate(`/user/${username}/blog/posts`, { replace: true });
    }
  }, [username, location.pathname, navigate, normalizedRouteFeedType]);

  // Feed-only refresh path: changing /user/:username/feed/:feedType updates only posts section.
  useEffect(() => {
    if (activeTab !== 'short') return;
    let cancelled = false;

    const refreshShortPostsForRouteFeed = async () => {
      setShortFeedType(normalizedRouteFeedType);
      setPostFilter(normalizedRoutePostFilter);
      setShortOffset(0);
      setShortStartPermlink(null);
      setLoadingTab(true);
      try {
        await fetchShortPosts(normalizedRouteFeedType, normalizedRoutePostFilter);
      } finally {
        if (!cancelled) setLoadingTab(false);
      }
    };

    refreshShortPostsForRouteFeed();
    return () => {
      cancelled = true;
    };
  }, [username, normalizedRouteFeedType, normalizedRoutePostFilter, fetchShortPosts, activeTab]);

  // Blog-only refresh path: changing /user/:username/blog/:blogType updates only blog section.
  useEffect(() => {
    if (activeTab !== 'blog') return;
    let cancelled = false;

    const refreshBlogPostsForRouteType = async () => {
      setBlogType(normalizedRouteBlogType);
      setBlogStartPermlink(null);
      try {
        await fetchBlogPosts(normalizedRouteBlogType);
      } finally {
        if (cancelled) return;
      }
    };

    refreshBlogPostsForRouteType();
    return () => {
      cancelled = true;
    };
  }, [username, normalizedRouteBlogType, fetchBlogPosts, activeTab]);

  const handleTabChange = (tab) => {
    if (tab === 'short') {
      navigate(`/user/${username}/feed/${shortFeedType || 'flex'}`);
      return;
    }
    navigate(`/user/${username}/blog/${blogType || 'posts'}`);
  };

  const handleBlogTypeChange = (type) => {
    const normalized = normalizeBlogType(type);
    if (normalized === normalizedRouteBlogType) return;
    navigate(`/user/${username}/blog/${normalized}`);
  };

  const visibleBlogPosts = (() => {
    const isPinnedPost = (post) => Boolean(post?.stats?.is_pinned || post?.is_profile_pinned);

    const pinnedKeys = new Set(profilePinnedPosts.map(p => `${p.author}/${p.permlink}`));
    
    // Tag normal blog posts if they happen to be pinned
    const taggedBlogPosts = blogPosts.map(post => {
      const key = `${post.author}/${post.permlink}`;
      if (pinnedKeys.has(key) || isPinnedPost(post)) {
        return {
          ...post,
          is_profile_pinned: true,
          stats: { ...(post.stats || {}), is_pinned: true }
        };
      }
      return post;
    });

    if (blogType === 'pinned') {
      return profilePinnedPosts.map(post => ({
        ...post,
        is_profile_pinned: true,
        stats: { ...(post.stats || {}), is_pinned: true }
      }));
    }

    if (blogType === 'posts' || blogType === 'all') {
      if (!showPinnedBlogPosts) {
        return taggedBlogPosts;
      }

      // If highlighted, prepend pinned posts and append the rest
      const topPinned = profilePinnedPosts.map(post => ({
        ...post,
        is_profile_pinned: true,
        stats: { ...(post.stats || {}), is_pinned: true }
      }));
      
      const rest = taggedBlogPosts.filter(post => !pinnedKeys.has(`${post.author}/${post.permlink}`));
      return [...topPinned, ...rest];
    }

    return taggedBlogPosts;
  })();

  const handleFollow = async () => {
    if (!isAuthenticated) {
      alert('Please login to follow users');
      return;
    }

    try {
      if (isFollowing) {
        await hiveService.unfollow(currentUser.username, username);
        setIsFollowing(false);
      } else {
        await hiveService.follow(currentUser.username, username);
        setIsFollowing(true);
      }
    } catch (err) {
      console.error('Follow error:', err);
      alert('Failed to update follow status');
    }
  };

  const handleMute = () => {
    if (isProfileMuted) {
      if (window.confirm(`Unmute @${username}?`)) {
        removeMutedUser(username);
      }
      return;
    }

    if (window.confirm(`Are you sure you want to mute user\n@${username}?`)) {
      addMutedUser(username);
    }
  };

  if (loading) {
    return (
      <>
        <SidebarHeader title={`@${username}`} hideTitle={true} onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        <div className="user-profile-container">
          <LoadingSpinner text="Loading profile..." />
        </div>
      </>
    );
  }

  if (error || !profile) {
    return (
      <>
        <SidebarHeader title={`@${username}`} hideTitle={true} onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        <div className="error-box">
          <p>{error || 'User not found'}</p>
        </div>
      </>
    );
  }

  const profileImage = profile.profile.profileImage || 
    `https://images.hive.blog/u/${username}/avatar`;

  const coverImage = profile.profile.coverImage || 
    'https://via.placeholder.com/600x200/1da1f2/ffffff?text=Hive';

  return (
    <>
      <SidebarHeader title={`@${username}`} hideTitle={true} onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      <div className="profile-container">
      <div
        className={`profile-header${profile.profile.coverImage ? ' profile-header--clickable' : ''}`}
        style={{ backgroundImage: `url(${coverImage})` }}
        onClick={profile.profile.coverImage ? () => setShowCoverLightbox(true) : undefined}
        role={profile.profile.coverImage ? 'button' : undefined}
        aria-label={profile.profile.coverImage ? 'View cover image' : undefined}
        tabIndex={profile.profile.coverImage ? 0 : undefined}
        onKeyDown={profile.profile.coverImage ? e => e.key === 'Enter' && setShowCoverLightbox(true) : undefined}
      >
        <div className="profile-header-overlay" />
        <div className="profile-info-top">
          <img
            src={profileImage}
            alt={username}
            className="profile-avatar"
            onClick={(e) => { e.stopPropagation(); setShowProfileImageLightbox(true); }}
            onError={(event) => {
              const fallback = `https://images.hive.blog/u/${encodeURIComponent(username)}/avatar`;
              if (event.currentTarget.src !== fallback) {
                event.currentTarget.src = fallback;
              } else {
                event.currentTarget.onerror = null;
                event.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=random`;
              }
            }}
          />
        </div>
      </div>

      <div className="profile-info">
        
        <div className="profile-details">
          <div className="profile-identity-row">
            <div className="profile-identity-text">
              <h2 className="profile-name">
                {profile.profile.name || `@${username}`}
              </h2>
              <p className="profile-username">@{username}</p>
            </div>
            <div className="profile-actions-right">
              {isAuthenticated && currentUser.username === username && (
                <button
                  className="edit-profile-btn"
                  onClick={() => navigate('/settings/profile')}
                >
                  Edit profile
                </button>
              )}
              {isAuthenticated && currentUser.username !== username && (
                <button
                  className="profile-add-to-list-btn"
                  onClick={() => setShowAddToListModal(true)}
                >
                  Add to list
                </button>
              )}
            </div>
          </div>
          
          {profile.profile.about && (
            <div className="profile-bio">
              <MarkdownRenderer content={profile.profile.about} disableCarousel={true} />
            </div>
          )}

          <div className="profile-stats">
            <div className="stat">
              <strong>{profile.postCount || 0}</strong>
              <Link to={`/user/${username}/blog/posts`} className="stat-link">Posts</Link>
            </div>
            <div className="stat">
              <strong>{profile.followerCount || 0}</strong>
              <Link to={`/user/${username}/followers`} className="stat-link">Followers</Link>
            </div>
            <div className="stat">
              <strong>{profile.followingCount || 0}</strong>
              <Link to={`/user/${username}/following`} className="stat-link">Following</Link>
            </div>
            <div className="stat">
              <strong>{profile.reputation}</strong>
              <span>Reputation</span>
            </div>
            {isAuthenticated && currentUser.username !== username && (
              <div className="profile-stats-action">
                <button className="profile-mute-btn" onClick={handleMute}>
                  {isProfileMuted ? 'Unmute' : 'Mute user'}
                </button>
              </div>
            )}
          </div>

          <div className="profile-mana-container">
            <div className="profile-mana-bar-wrapper">
              <div className="profile-mana-label">
                <span>Voting Mana</span>
                <span>{manaData?.voting || '...'}%</span>
              </div>
              <div className={`profile-mana-track ${!manaData ? 'loading' : ''}`}>
                <div className="profile-mana-fill voting" style={{ width: `${manaData?.voting || 0}%` }}></div>
              </div>
            </div>
            
            <div className="profile-mana-bar-wrapper">
              <div className="profile-mana-label">
                <span>Resource Credits</span>
                <span>{manaData?.rc || '...'}%</span>
              </div>
              <div className={`profile-mana-track ${!manaData ? 'loading' : ''}`}>
                <div className="profile-mana-fill rc" style={{ width: `${manaData?.rc || 0}%` }}></div>
              </div>
            </div>

            <button
              className={`profile-mana-refresh ${isRefreshingMana ? 'refreshing' : ''}`}
              onClick={handleRefreshMana}
              title="Refresh Mana"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </svg>
            </button>
          </div>

          {isAuthenticated && currentUser.username !== username && (
            <button
              onClick={handleFollow}
              className={`follow-btn ${isFollowing ? 'following' : ''}`}
            >
              {isFollowing ? 'Unfollow' : 'Follow'}
            </button>
          )}

          {/*isAuthenticated && currentUser.username === username && (
            <button
              onClick={() => setShowWalletInfo(true)}
              className="wallet-info-btn"
            >
              💰 View Wallet
            </button>
          )*/}

          <button
            onClick={() => setShowWalletInfo(true)}
            className="wallet-info-btn"
          >
            💰 View Wallet
          </button>

          <button
            className="raw-toggle"
            onClick={() => setShowRawHive(prev => !prev)}
          >
            {showRawHive ? 'Hide raw HIVE data' : 'Show raw HIVE data'}
          </button>

          {showRawHive && (
            <div className="raw-hive-data">
              <h4>Raw HIVE account data</h4>
              <pre className="raw-data-block">{JSON.stringify(profile.rawAccount || profile, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>

      {showWalletInfo && (
        <WalletInfo 
          username={username} 
          onClose={() => setShowWalletInfo(false)} 
        />
      )}

      {showAddToListModal && (
        <AddToListModal
          targetUsername={username}
          onClose={() => setShowAddToListModal(false)}
        />
      )}

      {showProfileImageLightbox && (
        <Lightbox
          images={[profileImage]}
          currentIndex={0}
          onClose={() => setShowProfileImageLightbox(false)}
        />
      )}

      {showCoverLightbox && (
        <Lightbox
          images={[profile.profile.coverImage]}
          currentIndex={0}
          onClose={() => setShowCoverLightbox(false)}
        />
      )}

      <div className="profile-posts">

        {(activeTab === 'followers' || activeTab === 'following') ? (
          <FollowList embedded={true} />
        ) : (
          <>
            <div className="content-tabs">
              <button 
            className={`tab-btn ${activeTab === 'short' ? 'active' : ''}`}
            onClick={() => handleTabChange('short')}
          >
            Short Posts
          </button>
          <button 
            className={`tab-btn ${activeTab === 'blog' ? 'active' : ''}`}
            onClick={() => handleTabChange('blog')}
          >
            Blog Posts
          </button>
        </div>

        {activeTab === 'short' && (
          <>
            <div className="profile-feed-selector mobile-only">
              <select
                className="profile-feed-dropdown"
                value={shortFeedType}
                onChange={(e) => handleFeedChange(e.target.value)}
                disabled={loadingTab}
                aria-label="Select profile feed"
              >
                {enabledFeeds.map((feed) => (
                  <option key={feed.key} value={feed.key}>
                    {feed.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="feed-tabs desktop-only">
              {enabledFeeds.map((feed) => (
                <button
                  key={feed.key}
                  className={`feed-tab ${shortFeedType === feed.key ? 'active' : ''}`}
                  onClick={() => handleFeedChange(feed.key)}
                  disabled={loadingTab}
                >
                  {loadingTab && shortFeedType === feed.key ? 'Loading...' : feed.label}
                </button>
              ))}
            </div>
            <div className="feed-tabs post-filter-tabs">
              <button
                className={`feed-tab ${postFilter === 'posts' ? 'active' : ''}`}
                onClick={() => handlePostFilterChange('posts')}
                disabled={loadingTab}
              >
                Top-level
              </button>
              <button
                className={`feed-tab ${postFilter === 'comments' ? 'active' : ''}`}
                onClick={() => handlePostFilterChange('comments')}
                disabled={loadingTab}
              >
                Only comments
              </button>
              <button
                className={`feed-tab ${postFilter === 'all' ? 'active' : ''}`}
                onClick={() => handlePostFilterChange('all')}
                disabled={loadingTab}
              >
                All
              </button>
            </div>
            {/*<h3>{enabledFeeds.find(feed => feed.key === shortFeedType)?.label === 'Flex' ? "Flex's" : enabledFeeds.find(feed => feed.key === shortFeedType)?.label} by @{username} <span className="source-badge">{shortPostSource === 'database' ? '(DB)' : '(Live)'}</span></h3>*/}
            <h3>{enabledFeeds.find(feed => feed.key === shortFeedType)?.label === 'Flex' ? "Flex's" : enabledFeeds.find(feed => feed.key === shortFeedType)?.label} by @{username}</h3>
            {loadingTab ? (
              <div className="tab-loading">
                <div className="spinner"></div>
                <p>Loading posts...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="empty-state">
                <p>No {shortFeedType.charAt(0).toUpperCase() + shortFeedType.slice(1)} {shortFeedType === 'shortapp' ? 'posts ' : ''}yet</p>
              </div>
            ) : (
              <>
                <div className="posts-list">
                  {posts.map((post, index) => (
                    <PostCard 
                      key={`${post.author}-${post.permlink}-${index}`} 
                      post={post} 
                      showActions={true}
                      onCommentPosted={handleCommentPosted}
                      showReplyNotice={true}
                    />
                  ))}
                </div>
                
                {hasMoreShort && (
                  <div className="load-more-container" ref={autoload_more ? loadMoreShortObserverRef : null}>
                    {loadingMoreShort ? (
                      <>
                        <PostSkeleton />
                        <PostSkeleton />
                      </>
                    ) : !autoload_more && (
                      <button 
                        className="load-more-btn" 
                        onClick={loadMoreShortPosts}
                        disabled={loadingMoreShort}
                      >
                        Load More ({posts.length} posts loaded)
                      </button>
                    )}
                  </div>
                )}
                
                {!hasMoreShort && (
                  <div className="end-message">
                    <p>No more posts to load</p>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {activeTab === 'blog' && (
          <>
            <div className="profile-feed-selector mobile-only">
              <select
                className="profile-feed-dropdown"
                value={blogType}
                onChange={(e) => handleBlogTypeChange(e.target.value)}
                disabled={loadingBlogTab}
                aria-label="Select blog view"
              >
                <option value="posts">Original articles</option>
                <option value="all">All (incl. reflux)</option>
                <option value="reblogs">Reflux only</option>
                <option value="pinned">Pinned only</option>
                <option value="comments">Comments</option>
              </select>
            </div>
            
            <div className="feed-tabs desktop-only">
              <button 
                className={`feed-tab ${blogType === 'posts' ? 'active' : ''}`}
                onClick={() => handleBlogTypeChange('posts')}
                disabled={loadingBlogTab}
              >
                {loadingBlogTab && blogType === 'posts' ? 'Loading...' : 'Original articles'}
              </button>
              <button 
                className={`feed-tab ${blogType === 'all' ? 'active' : ''}`}
                onClick={() => handleBlogTypeChange('all')}
                disabled={loadingBlogTab}
              >
                {loadingBlogTab && blogType === 'all' ? 'Loading...' : 'All (incl. reflux)'}
              </button>
              <button
                className={`feed-tab ${blogType === 'reblogs' ? 'active' : ''}`}
                onClick={() => handleBlogTypeChange('reblogs')}
                disabled={loadingBlogTab}
              >
                {loadingBlogTab && blogType === 'reblogs' ? 'Loading...' : 'Reflux only'}
              </button>
              <button
                className={`feed-tab ${blogType === 'pinned' ? 'active' : ''}`}
                onClick={() => handleBlogTypeChange('pinned')}
                disabled={loadingBlogTab}
              >
                {loadingBlogTab && blogType === 'pinned' ? 'Loading...' : 'Pinned only'}
              </button>
              <button
                className={`feed-tab ${blogType === 'comments' ? 'active' : ''}`}
                onClick={() => handleBlogTypeChange('comments')}
                disabled={loadingBlogTab}
              >
                {loadingBlogTab && blogType === 'comments' ? 'Loading...' : 'Comments'}
              </button>
            </div>
            {(blogType === 'posts' || blogType === 'all') && (
              <div className="profile-pinned-toggle-row">
                <h3>{blogType === 'reblogs' ? 'Refluxed posts' : blogType === 'pinned' ? 'Pinned posts' : 'Blog Posts'} by @{username}</h3>
                <div>
                  <span className="profile-pinned-toggle-label">Highlight pinned posts</span>
                  <button
                    type="button"
                    className={`profile-pinned-toggle ${showPinnedBlogPosts ? 'on' : 'off'}`}
                    onClick={() => setShowPinnedBlogPosts((prev) => !prev)}
                    aria-label="Toggle pinned posts"
                  >
                    <span className={`profile-pinned-option ${!showPinnedBlogPosts ? 'active' : ''}`}>Off</span>
                    <span className={`profile-pinned-option ${showPinnedBlogPosts ? 'active' : ''}`}>On</span>
                    <span className="profile-pinned-slider" />
                  </button>
                </div>
              </div>
            )}
            {(blogType === 'reblogs' || blogType === 'pinned' || blogType === 'comments') && (
              <h3>
                {blogType === 'reblogs' ? 'Refluxed posts' : blogType === 'pinned' ? 'Pinned posts' : 'Comments'} by @{username}
              </h3>
            )}

            {loadingBlogTab || ((blogType === 'pinned' || showPinnedBlogPosts) && loadingProfilePinnedPosts) ? (
              <div className="tab-loading">
                <div className="spinner"></div>
                <p>Loading posts...</p>
              </div>
            ) : visibleBlogPosts.length === 0 ? (
              <div className="empty-state">
                <p>{blogType === 'reblogs' ? 'No refluxed posts yet' : blogType === 'pinned' ? 'No pinned posts yet' : blogType === 'comments' ? 'No comments yet' : 'No blog posts yet'}</p>
              </div>
            ) : (
              <>
                <div className="posts-list">
                  {visibleBlogPosts.map((post, index) => (
                    <div key={`${post.author}-${post.permlink}-${index}`} className="profile-pinned-post-wrap">
                      <PostCard 
                        post={post} 
                        showActions={true}
                        onCommentPosted={handleCommentPosted}
                        showReplyNotice={blogType === 'comments'}
                        showReplyNoticeForTopLevel={blogType === 'comments'}
                        inactivePinned={!showPinnedBlogPosts}
                      />
                    </div>
                  ))}
                </div>
                
                {hasMoreBlog && (
                  <div className="load-more-container" ref={autoload_more ? loadMoreBlogObserverRef : null}>
                    {loadingMoreBlog ? (
                      <>
                        <PostSkeleton />
                        <PostSkeleton />
                      </>
                    ) : !autoload_more && (
                      <button 
                        className="load-more-btn" 
                        onClick={loadMoreBlogPosts}
                        disabled={loadingMoreBlog}
                      >
                        Load More ({visibleBlogPosts.length} posts loaded)
                      </button>
                    )}
                  </div>
                )}
                
                {!hasMoreBlog && (
                  <div className="end-message">
                    <p>No more posts to load</p>
                  </div>
                )}
              </>
            )}
          </>
        )}
        </>
        )}
      </div>
    </div>
    </>
  );
};

export default UserProfile;
